import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import InputField from 'dashboard/components/FormControls/InputField';
import {
  ReactCreatableSelectField,
  ReactCreatableSelectFieldProps,
} from 'dashboard/components/FormControls/ReactSelectField';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import Banner from 'dashboard/components-lib/Banner';
import { splitEmailAddresses } from 'dashboard/forms/FormSendCaseEmail/utils';
import { Environment } from 'dashboard/models/environment';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import { ApiModel } from 'lib/interfaces/api';
import { isRequired, matchesRegexp, withMessage } from 'lib/utils/formValidation';

const validate = {
  emailAddress: [
    withMessage('Please enter an email address', isRequired),
    withMessage('The email you entered is not valid', matchesRegexp(EMAIL_ADDRESS_REGEX)),
  ],
  emailCcAddress: [
    withMessage(
      'The email address(es) you entered are not valid',
      (emailAddresses) =>
        !emailAddresses ||
        (Array.isArray(emailAddresses) &&
          emailAddresses.every((email) => matchesRegexp(EMAIL_ADDRESS_REGEX)(email))),
    ),
  ],
};

const StyledBanner = styled(Banner)`
  padding: 0px ${Spacing[4]};
`;

interface FormSendInquiryEmailProps {
  handleCancel: () => void;
  currentEnvironment: Environment;
}

const FormSendInquiryEmail = ({
  handleCancel,
  handleSubmit,
  submitting,
  currentEnvironment,
}: FormSendInquiryEmailProps & InjectedFormProps<{}, FormSendInquiryEmailProps>) => {
  return (
    <form onSubmit={handleSubmit}>
      {currentEnvironment.type === ApiModel.EnvironmentSandbox && (
        <>
          <StyledBanner icon={<CircleInfoIcon />}>
            Sandbox is for testing and demo purposes. On production you can email inquiry links to
            anyone!
          </StyledBanner>
          <Spacer height={2} />
        </>
      )}
      <Field
        component={InputField}
        name='email-address'
        id='form-send-inquiry-email__email_address'
        type='email'
        data-test='form-send-inquiry-email__email_address'
        validate={validate.emailAddress}
        disabled={currentEnvironment.type === ApiModel.EnvironmentSandbox || submitting}
        placeholder='recipient@email.com'
        label='Recipient'
        autoFocus={true}
      />
      <Field
        name='email-cc-addresses'
        id='form-send-inquiry-email__email_cc_addresses'
        data-test='form-send-inquiry-email__email_cc_addresses'
        type='select-multiple'
        validate={validate.emailCcAddress}
        normalize={(value: string[] | undefined) => value && splitEmailAddresses(value)}
        component={(props: ReactCreatableSelectFieldProps) => (
          <ReactCreatableSelectField
            {...props}
            isMulti={true}
            disabled={currentEnvironment.type === ApiModel.EnvironmentSandbox || submitting}
            placeholder='cc-recipient@email.com'
            label='Cc'
            options={(props.input.value || []).map((email: string) => ({
              label: email,
              value: email,
            }))}
          />
        )}
      />
      <Field
        component={InputField}
        name='email-subject'
        id='form-send-inquiry-email__email_subject'
        data-test='form-send-inquiry-email__email_subject'
        disabled={submitting}
        label='Subject'
      />
      <Field
        component={InputField}
        name='email-greeting'
        id='form-send-inquiry-email__email_greeting'
        data-test='form-send-inquiry-email__email_greeting'
        disabled={submitting}
        label='Greeting'
      />
      <Field
        component={TextAreaField}
        name='email-body'
        id='form-send-inquiry-email__email_body'
        data-test='form-send-inquiry-email__email_body'
        disabled={submitting}
        label='Body'
      />
      <Field
        component={InputField}
        name='email-cta'
        id='form-send-inquiry-email__email_cta'
        data-test='form-send-inquiry-email__email_cta'
        disabled={submitting}
        label='Call to action'
      />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-send-inquiry-email__cancel'
          data-test='form-send-inquiry-email__cancel'
          disabled={submitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-send-inquiry-email__send'
          disabled={submitting}
          isPending={submitting}
        >
          Send
        </Button>
      </HStack>
    </form>
  );
};

export interface SendInquiryEmailFormData {
  'email-address'?: string;
  'email-cc-addresses'?: string[];
  'email-subject'?: string;
  'email-greeting'?: string;
  'email-body'?: string;
  'email-cta'?: string;
}

export default reduxForm<SendInquiryEmailFormData, FormSendInquiryEmailProps>({
  form: 'sendInquiryEmail',
})(FormSendInquiryEmail);
